import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Message } from 'semantic-ui-react';
import { useComponentManager } from '../../providers/ComponentManagerProvider';
import { componentLabel } from './componentLabel';

const yesNo = value => (value ? 'Yes' : 'No');
const orDash = value => (value == null || value === '' ? '--' : value);

const FIELDS = [
    { key: 'num', label: 'Number', render: d => orDash(d.num) },
    { key: 'description', label: 'Description', render: d => orDash(d.description) },
    { key: 'haystack_tag', label: 'Haystack Tag', render: d => orDash(d.haystack_tag) },
    { key: 'component_type_name', label: 'Type', render: d => orDash(d.component_type_name) },
    { key: 'selectable', label: 'Selectable', render: d => yesNo(d.selectable) },
    {
        key: 'variable_quantity',
        label: 'Variable Quantity',
        render: d => yesNo(d.variable_quantity),
    },
    { key: 'group', label: 'Group', render: d => yesNo(d.group) },
];

export const StructureDetailPanel = ({ onAddChild, onEdit, onDelete, deleting, refreshKey }) => {
    const { selectedComponent, getComponentDetails } = useComponentManager();

    const [details, setDetails] = useState(null);
    const [loading, setLoading] = useState(false);
    const [notFound, setNotFound] = useState(false);
    const [error, setError] = useState(false);

    const detailToken = useRef(0);

    const componentId = selectedComponent?.id;

    const loadDetails = useCallback(() => {
        setNotFound(false);
        setError(false);

        if (!componentId) {
            setDetails(null);
            setLoading(false);
            return;
        }

        const token = (detailToken.current += 1);
        setLoading(true);
        getComponentDetails(componentId)
            .then(res => {
                if (token !== detailToken.current) return;
                setDetails(res?.data ?? null);
                setLoading(false);
            })
            .catch(err => {
                if (token !== detailToken.current) return;
                setLoading(false);
                setDetails(null);
                if (err?.response?.status === 404) {
                    setNotFound(true);
                } else {
                    setError(true);
                }
            });
    }, [componentId, getComponentDetails]);

    useEffect(() => {
        loadDetails();
    }, [loadDetails, refreshKey]);

    if (!selectedComponent) {
        return (
            <div className="component-manager-detail" data-testid="structure-detail-empty">
                <p className="component-manager-detail-placeholder">
                    Select a component to view its details.
                </p>
            </div>
        );
    }

    return (
        <div className="component-manager-detail" data-testid="structure-detail">
            <div className="structure-detail-header">
                <h2 className="component-manager-detail-title">{componentLabel(selectedComponent)}</h2>
                {!notFound && (onEdit || onAddChild || onDelete) && (
                    <div className="structure-detail-actions">
                        {onDelete && (
                            <Button
                                size="small"
                                onClick={onDelete}
                                loading={deleting}
                                disabled={deleting}
                                data-testid="delete-component-button"
                            >
                                Delete
                            </Button>
                        )}
                        {onEdit && (
                            <Button
                                size="small"
                                onClick={onEdit}
                                data-testid="edit-component-button"
                            >
                                Edit
                            </Button>
                        )}
                        {onAddChild && (
                            <Button
                                primary
                                size="small"
                                onClick={onAddChild}
                                data-testid="add-child-button"
                            >
                                Add Child
                            </Button>
                        )}
                    </div>
                )}
            </div>

            {loading && !details && !notFound && !error && (
                <p
                    className="component-manager-detail-note"
                    data-testid="structure-detail-loading"
                    role="status"
                    aria-live="polite"
                >
                    Loading details...
                </p>
            )}

            {notFound && (
                <Message negative role="alert" data-testid="structure-detail-notfound">
                    <Message.Header>This component no longer exists</Message.Header>
                    <p>It may have been deleted. Refresh the tree to update the view.</p>
                </Message>
            )}

            {error && (
                <Message negative role="alert" data-testid="structure-detail-error">
                    <Message.Header>Failed to load component details</Message.Header>
                    <Button
                        basic
                        size="small"
                        onClick={loadDetails}
                        style={{ marginTop: '0.5rem' }}
                    >
                        Retry
                    </Button>
                </Message>
            )}

            {details && !notFound && !error && (
                <dl
                    className="structure-detail-fields"
                    data-testid="structure-detail-fields"
                    aria-busy={loading}
                >
                    {FIELDS.map(field => (
                        <div className="structure-detail-field" key={field.key}>
                            <dt>{field.label}</dt>
                            <dd data-testid={`structure-field-${field.key}`}>
                                {field.render(details)}
                            </dd>
                        </div>
                    ))}
                </dl>
            )}
        </div>
    );
};
